import { Box, Button, Checkbox, Dialog, DialogTitle, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import SelectArrow from '../styles/icons/SelectArrow';
import useAutomations from '../hooks/automationhooks/useAutomations';
import { useParams } from 'react-router-dom';

export default function TemplateCreation() {
    const {
        handleCreateSmsTemplate,
        handleCreateWhatsappTemplate,
        handleGetSmsTemplates,
        handleGetWhatsappTemplates,
        handleGetInforuBlockLink,
    } = useAutomations();
    const { TemplateType, brandId } = useParams();

    // Route uses values like "smsMessage" / "whatsappMessage" (and sometimes "SMS" / "Whatsapp").
    // Normalize so UI/logic doesn't depend on exact casing/string.
    const templateTypeNorm = (TemplateType || "").toLowerCase();
    const isWhatsapp =
        templateTypeNorm === "whatsapp" || templateTypeNorm === "whatsappmessage" || templateTypeNorm.includes("whatsapp");
    const isSms =
        templateTypeNorm === "sms" || templateTypeNorm === "smsmessage" || templateTypeNorm.includes("sms");

    const [text, setText] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [nameText, setNameText] = useState("");
    const [nameTouched, setNameTouched] = useState(false);
    const [contentTouched, setContentTouched] = useState(false);
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [validationError, setValidationError] = useState("");

    const [includeRemoveLink, setIncludeRemoveLink] = useState(false);
    const [blockText, setBlockText] = useState("");
    const [blockTextLoading, setBlockTextLoading] = useState(false);
    const [blockTextError, setBlockTextError] = useState("");

    // Cache InforU BlockText per brand so re-checking doesn't re-call API (until refresh).
    const blockTextCacheRef = useRef(new Map());

    // Live validation on content change
    useEffect(() => {
        if (isWhatsapp && contentTouched) {
            validateWhatsappTemplate(); // This will set validation error text
        }
    }, [text, selectedVariables]);

    const handleNameChange = (e) => {
        setNameText(e.target.value);
    };

    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const templateData = Object.fromEntries(formData.entries());

        if (isWhatsapp) {
            if (!validateWhatsappTemplate()) {
                return;
            }
            templateData.templateVariables = selectedVariables;
            await handleCreateWhatsappTemplate(brandId, templateData);
            setDialogOpen(true)
            handleGetWhatsappTemplates(brandId)
        } else {
            if (isSms && includeRemoveLink) {
                const main = (text || "").replace(/\s+$/, "");
                const suffix = (blockText || "").trim();
                templateData.templateContent = suffix ? `${main}\n\n${suffix}` : main;
            }
            await handleCreateSmsTemplate(brandId, templateData);
            setDialogOpen(true)
            handleGetSmsTemplates(brandId)
        }
    };

    // Pure checker (safe to use in render)
    const isValidWhatsappTemplateStructure = () => {
        const tokenMatches = text.match(/\[#(\d+)#\]/g);
        if (!tokenMatches) return false;

        const numbers = tokenMatches.map(t => parseInt(t.match(/\[#(\d+)#\]/)[1]));
        const isSequential = numbers.every((n, i) => n === i + 1);
        const allSelected = selectedVariables.length === numbers.length && selectedVariables.every(v => v);

        return isSequential && allSelected;
    };

    // Form submission + live error text
    const validateWhatsappTemplate = () => {
        const tokenMatches = text.match(/\[#(\d+)#\]/g);

        // If no tokens at all, it's still valid
        if (!tokenMatches) {
            setValidationError("");
            return true;
        }

        const numbers = tokenMatches.map(t => parseInt(t.match(/\[#(\d+)#\]/)[1]));
        const isSequential = numbers.every((n, i) => n === i + 1);
        const allSelected = selectedVariables.length === numbers.length && selectedVariables.every(v => v);

        if (!isSequential) {
            setValidationError("Variables must be numbered in order: [#1#], [#2#], etc.");
            return false;
        }

        if (!allSelected) {
            setValidationError("Please select a value for each variable.");
            return false;
        }

        setValidationError("");
        return true;
    };

    const renderSelects = (text) => {
        const matches = text.match(/\[#(.*?)#\]/g);
        if (!matches) return null;

        return matches.map((part, i) => {
            const label = part.replace(/\[#(.*?)#\]/g, '$1');
            return (
                <div key={i}>
                    <span>{label}: </span>
                    {isWhatsapp && (
                        <Select
                            size="small"
                            IconComponent={SelectArrow}
                            value={selectedVariables[i] || ""}
                            onChange={(e) => {
                                const newSelections = [...selectedVariables];
                                newSelections[i] = e.target.value;
                                setSelectedVariables(newSelections);
                            }}
                            sx={{
                                margin: "0 5px",
                                fontSize: "0.875rem",
                                mt: 0.5,
                                pr: 0.5,
                                borderRadius: 4,
                                minWidth: 100
                            }}
                        >
                            <MenuItem value="" disabled>Select option</MenuItem>
                            <MenuItem value="FirstName">First Name</MenuItem>
                            <MenuItem value="LastName">Last Name</MenuItem>
                            <MenuItem value="FullName">Full Name</MenuItem>
                            <MenuItem value="Id">Id</MenuItem>
                            <MenuItem value="Phone">Phone</MenuItem>
                            <MenuItem value="QrCode">QR Code</MenuItem>
                        </Select>
                    )}
                </div>
            );
        });
    };

    return (
        <>
            <Box
                component='form'
                onSubmit={handleSubmit}
                sx={{
                    height: '100%',
                    minHeight: '100%'
                }}
            >
                <Box>
                    <Box sx={{ mb: 2, mt: 2, ml: 2 }}>
                        <Typography color='companyBlue' variant="h6" gutterBottom>
                            {TemplateType} Template Creation
                        </Typography>
                    </Box>
                </Box>
                {/* Title section with dark background (default) */}

                {/* Form content section with light grey background */}
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 3,
                        borderRadius: 1,
                        mb: 2,
                        height: '100%'
                    }}
                >
                    <InputLabel>Template Name</InputLabel>
                    <TextField
                        name='templateName'
                        error={!nameText && nameTouched}
                        onClick={() => setNameTouched(true)}
                        onInput={handleNameChange}
                        sx={{ minWidth: 500 }}
                    />
                    <br /><br /><br />
                    <div>Instructions: wrap all variables in [# #].</div>
                    {TemplateType === "Whatsapp" && (
                        <div>
                            Message content with placeholders for parameters, parameters are written in the following form [#1#] [#2#] and so on. <br />
                            The template must comply with the following regulations:
                            <ol>
                                <li>Variables cannot be entered in a template one after the other. Variables must be separated by one or more words.</li>
                                <li>Do not use special characters such as &amp;, +, &gt;, &lt; in the template text.</li>
                                <li>Do not place a variable or a link at the beginning or at the end of the text.</li>
                                <li>It is not possible to enter several empty lines in a row in order to create a space between paragraphs in the template.</li>
                            </ol>
                        </div>
                    )}
                    <InputLabel>Template Content</InputLabel>
                    <Box sx={{ width: 500 }}>
                        <TextField
                            name='templateContent'
                            error={!text && contentTouched}
                            onClick={() => setContentTouched(true)}
                            value={text}
                            onChange={handleTextChange}
                            multiline
                            rows={7}
                            size='medium'
                            fullWidth
                        />

                        {isSms && (
                            <>
                                <FormControlLabel
                                    sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                                    control={
                                        <Checkbox
                                            checked={includeRemoveLink}
                                            onChange={async (e) => {
                                                const checked = e.target.checked;
                                                setIncludeRemoveLink(checked);
                                                setBlockTextError("");

                                                if (!checked) {
                                                    setBlockText("");
                                                    return;
                                                }

                                                const cacheKey = `${brandId ?? ""}:withPhone`;
                                                const cached = blockTextCacheRef.current.get(cacheKey);
                                                if (cached) {
                                                    setBlockText(cached);
                                                    return;
                                                }

                                                try {
                                                    setBlockTextLoading(true);
                                                    const res = await handleGetInforuBlockLink(brandId, true);
                                                    const bt = res?.data?.Data?.BlockText || "";
                                                    if (!bt) {
                                                        setBlockTextError("InforU returned empty BlockText.");
                                                    } else {
                                                        blockTextCacheRef.current.set(cacheKey, bt);
                                                    }
                                                    setBlockText(bt);
                                                } catch (err) {
                                                    console.error("GetBlockLink failed:", err);
                                                    setBlockTextError("Failed to load remove link text from InforU.");
                                                    setBlockText("");
                                                } finally {
                                                    setBlockTextLoading(false);
                                                }
                                            }}
                                        />
                                    }
                                    label="Add remove link (InforU opt-out)"
                                />

                                {includeRemoveLink ? (
                                    <Box sx={{ mt: 1 }}>
                                        <InputLabel>Auto-added (not editable)</InputLabel>
                                        <TextField
                                            value={blockTextLoading ? "Loading..." : (blockText || "")}
                                            multiline
                                            fullWidth
                                            disabled
                                            size='medium'
                                        />
                                        {blockTextError ? (
                                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                                {blockTextError}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>
                    {isWhatsapp && validationError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {validationError}
                        </Typography>
                    )}

                    <div>{renderSelects(text)}</div>
                    {/* Button section */}
                    <Button
                        color='companyBlue'
                        aria-label='submit'
                        type='submit'
                        variant='contained'
                        disabled={
                            !nameText || !text ||
                            (isWhatsapp && !isValidWhatsappTemplateStructure()) ||
                            (isSms && includeRemoveLink && (blockTextLoading || !blockText))
                        }
                        sx={{
                            mt: 2,
                            borderRadius: '50px',
                            height: '40px',
                            textTransform: 'none',
                        }}
                    >
                        <Typography color='text.primary'>Submit</Typography>
                    </Button>
                </Box>
            </Box>
            <Dialog open={dialogOpen}>
                <DialogTitle>
                        Template created successfully. You may close the window.
                </DialogTitle>
            </Dialog>
        </>
    );
}